import { useMemo, useState } from 'react';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import type { CatalogRole } from '../../../services/iam';
import type { PermissionLookup } from '../iamLookups';
import { AccessCard } from '../primitives/AccessCard';
import { AccessCardSubsection } from '../primitives/AccessCardSubsection';
import { Chain } from '../primitives/Chain';
import { PermName } from '../primitives/PermName';

function familyLabel(group: string): string {
    return group.charAt(0).toUpperCase() + group.slice(1);
}

interface FlattenedByFamily {
    readonly family: string;
    readonly items: readonly CatalogRole['flattened'][number][];
}

function groupFlattenedByFamily(
    flattened: CatalogRole['flattened'],
    permissionLookup: PermissionLookup
): FlattenedByFamily[] {
    const groups = new Map<string, CatalogRole['flattened'][number][]>();
    for (const item of flattened) {
        const family = permissionLookup.get(item.permission)?.group ?? 'unknown';
        const bucket = groups.get(family) ?? [];
        bucket.push(item);
        groups.set(family, bucket);
    }
    return Array.from(groups.entries())
        .map(([family, items]) => ({ family, items }))
        .sort((a, b) => a.family.localeCompare(b.family));
}

interface RolePermissionsCardProps {
    role: CatalogRole;
    permissionLookup: PermissionLookup;
}

// Card 3: every permission this role's flattened set produces, foldable by
// family, default open (short lists here vs. Routes page's ~350 routes).
export function RolePermissionsCard({ role, permissionLookup }: RolePermissionsCardProps) {
    const byFamily = useMemo(
        () => groupFlattenedByFamily(role.flattened, permissionLookup),
        [role, permissionLookup]
    );
    const [closedFamilies, setClosedFamilies] = useState<ReadonlySet<string>>(new Set());

    const toggleFamily = (family: string) => {
        setClosedFamilies((prev) => {
            const next = new Set(prev);
            if (next.has(family)) {
                next.delete(family);
            } else {
                next.add(family);
            }
            return next;
        });
    };

    return (
        <AccessCard
            icon={ListBulletIcon}
            title="Permissions this gives access to"
            count={role.flattened.length}
            noBodyPadding
            bodyClassName="flex flex-col"
        >
            {role.flattened.length === 0 && (
                <p className="text-sm text-gray-400 px-3.5 py-4">
                    Confers nothing yet. The permissions this{' '}
                    {role.kind === 'bundle' ? 'bundle' : 'role'} will hold are not part of the
                    current migration slice.
                </p>
            )}
            {byFamily.map((group, index) => (
                <AccessCardSubsection
                    key={group.family}
                    label={familyLabel(group.family)}
                    count={group.items.length}
                    divider={index > 0}
                    collapsible
                    open={!closedFamilies.has(group.family)}
                    onToggle={() => toggleFamily(group.family)}
                >
                    {group.items.map((item) => {
                        const routeCount = permissionLookup.get(item.permission)?.routeCount ?? 0;
                        const [primaryChain, ...restChains] = item.chains;
                        return (
                            <div
                                key={item.permission}
                                className="flex flex-col gap-1 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0"
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <PermName
                                        permission={item.permission}
                                        group={group.family}
                                        wired={routeCount > 0}
                                    />
                                    <span className="text-[11px] text-gray-400 shrink-0">
                                        {routeCount} route{routeCount === 1 ? '' : 's'}
                                    </span>
                                </div>
                                {primaryChain && <Chain chain={primaryChain} className="pl-3" />}
                                {restChains.map((chain, chainIndex) => (
                                    <div
                                        key={chainIndex}
                                        className="flex items-center gap-1.5 pl-3 opacity-50"
                                    >
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                            also
                                        </span>
                                        <Chain chain={chain} />
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </AccessCardSubsection>
            ))}
        </AccessCard>
    );
}
